export enum TicketStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in-progress',
  CLOSED = 'closed',
  NOT_APPLICABLE = 'not-applicable'
}

export enum TicketPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high'
}

// role type -> label
export const SUPPORT_ROLE_LABELS: { [roleType: string]: string } = {
  broker: 'Broker',
  company: 'Vendor',
  fundingsource: 'Funding Source',
  member: 'Member',
};

export const TICKET_STATUS_OPTIONS = [
  { value: TicketStatus.PENDING, label: 'Pending', badge: 'badge-warning' },
  { value: TicketStatus.IN_PROGRESS, label: 'In Progress', badge: 'badge-primary' },
  { value: TicketStatus.CLOSED, label: 'Closed', badge: 'badge-success' },
  { value: TicketStatus.NOT_APPLICABLE, label: 'Not Applicable', badge: 'badge-secondary' },
];

export const TICKET_PRIORITY_OPTIONS = [
  { value: TicketPriority.LOW, label: 'Low', badge: 'badge-secondary' },
  { value: TicketPriority.MEDIUM, label: 'Medium', badge: 'badge-warning' },
  { value: TicketPriority.HIGH, label: 'High', badge: 'badge-danger' },
];

// what a ticket is about: general (noRecords), or a record of a type, e.g. a trip
// recordLabel: what its records are called when not the option's label, e.g. the billed trips of billing
// scopes: tenant types it is for, e.g. billing is with vendors and funding sources, same rules as the api
export const SUPPORT_REGARDING_OPTIONS = [
  { value: 'general', label: 'General', noRecords: true, scopes: ['company', 'fundingsource', 'member'] },
  { value: 'billing', label: 'Billing', recordLabel: 'Trip', scopes: ['company'] },
  { value: 'fundingsourceBilling', label: 'Billing', recordLabel: 'Trip', scopes: ['fundingsource'] },
  { value: 'trip', label: 'Trip', scopes: ['company', 'fundingsource', 'member'] },
  { value: 'company', label: 'Company', scopes: ['company'] },
  { value: 'vehicle', label: 'Vehicle', scopes: ['company'] },
  { value: 'driver', label: 'Driver', scopes: ['company'] },
];

// references opened in the trip details: a trip, or the vendor or funding source bill of one
export const SUPPORT_TRIP_SOURCES: { [referenceType: string]: string } = {
  trip: 'trips',
  billing: 'billing',
  fundingsourceBilling: 'fundingsourceBilling',
};

export function getRegardingOptions(scopeType: string) {
  return SUPPORT_REGARDING_OPTIONS.filter((option) => option.scopes.includes(scopeType));
}

export const SUPPORT_CONTACT_TYPES = ['company', 'fundingsource', 'member']
  .map((value) => ({ value, label: SUPPORT_ROLE_LABELS[value] }));

export function isBrokerStaff(user: any): boolean {
  return Boolean(user && user.role && user.role.type === 'broker');
}

// tenants of a ticket or chat, shown with C, FS and M badges as in the users list
export const SUPPORT_SCOPE_BADGES = [
  { type: 'company', short: 'C', label: 'Company', badge: 'badge-success', nameField: 'name' },
  { type: 'fundingsource', short: 'FS', label: 'Funding Source', badge: 'badge-secondary', nameField: 'title' },
  { type: 'member', short: 'M', label: 'Member', badge: 'badge-warning', nameField: 'displayName' },
];

export function getAssociations(scopeFields: any) {
  return SUPPORT_SCOPE_BADGES
    .filter((scope) => scopeFields[scope.type])
    .map((scope) => ({ ...scope, name: scopeFields[scope.type][scope.nameField] }));
}

export function getScopeName(scopeFields: any): string {
  return getAssociations(scopeFields)[0]?.name || '';
}

// worked out once instead of on every change detection
export function prepareTicket(ticket: any) {
  ticket.statusOption = TICKET_STATUS_OPTIONS.find((option) => option.value === ticket.status);
  ticket.priorityOption = TICKET_PRIORITY_OPTIONS.find((option) => option.value === ticket.priority);
  ticket.associations = getAssociations(ticket);
  return ticket;
}

// from a ticket:updated socket event
export function applyTicketUpdate(ticket: any, data: any) {
  Object.assign(ticket, { status: data.status, priority: data.priority });
  return prepareTicket(ticket);
}

export function trackById(index: number, item: any) {
  return item.id;
}

// records a page of each support list, sent to the api as the limit
export const SUPPORT_PAGE_SIZES = {
  // the latest ones, the rest are found by searching
  references: 10,
  chats: 30,
  messages: 30,
};

// same limits as the api
const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;

export function getAttachmentsError(files: File[]): string | null {
  if (files.length > MAX_ATTACHMENTS) {
    return `You can upload up to ${MAX_ATTACHMENTS} files at a time`;
  }
  const largeFile = files.find((file) => file.size > MAX_ATTACHMENT_SIZE);
  if (largeFile) {
    return `${largeFile.name} is larger than 10 MB`;
  }
  return null;
}
